/**
 * GroundingExercise — Post-session nervous system regulation.
 *
 * Shown at Close when a session was heavy (intensity ≥ 7 or grief/shame/anger/helplessness/
 * despair/terror/dread). Sage spec: give the user an orienting cue before re-entering their
 * day, or you risk leaving them in a sympathetic/dorsal-activated state.
 *
 * Three exercise options (user picks one):
 *   1. Box breath   — 4-4-4-4 breathing, 4 rounds, animated circle
 *   2. 5-4-3-2-1    — sensory grounding, one sense at a time
 *   3. Feet + weight — static body-scan text
 *
 * Always skippable. Default-present, not forced.
 *
 * End copy (Sage spec verbatim): "You're back. Take what served you. Leave the rest."
 */
import { useEffect, useState } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { BACK_DEPTH, useScreenTrail, useSessionBack } from '../../lib/sessionBack'
import type { ScreenKey } from '../../lib/screenIds'
import { useTranslation } from 'react-i18next'

interface GroundingExerciseProps {
  /** Called when user completes any exercise or clicks Skip */
  onDismiss: () => void
}

type ExerciseState = 'choosing' | 'box-breath' | 'senses' | 'feet' | 'done'

/** Which numbered screen each state shows (see lib/screenIds.ts). */
const SCREEN_OF: Record<ExerciseState, ScreenKey> = {
  'choosing':   'close.ground.choose',
  'box-breath': 'close.ground.breath',
  'senses':     'close.ground.senses',
  'feet':       'close.ground.feet',
  'done':       'close.ground.done',
}

// ─── Box Breath constants ──────────────────────────────────────────────────────
// 4 phases × 4 seconds × 4 rounds = 64 steps total (1 step = 1 second)
const BOX_BREATH_TOTAL_STEPS = 64
const BREATH_PHASE_LABELS = ['Breathe in', 'Hold', 'Breathe out', 'Hold']
// Circle px diameter for expanded (inhale/hold-in) vs resting (exhale/hold-out) states.
// The CSS transition handles the animation between changes.
const CIRCLE_SIZE_BY_PHASE = [140, 140, 80, 80]  // indices match BREATH_PHASE_LABELS
const CIRCLE_TRANSITION_BY_PHASE = ['4s ease-in-out', '0.1s', '4s ease-in-out', '0.1s']

// ─── 5-4-3-2-1 Senses steps ──────────────────────────────────────────────────
// Only `n` is language-independent. The label and instruction below are the
// English fallback — the displayed text is read from the locale arrays
// grounding.senseLabels / grounding.sensesSteps inside the component.
const SENSES_STEPS = [
  { n: 5, label: 'See', instruction: 'Name 5 things you can see around you right now. Take your time with each one.' },
  { n: 4, label: 'Hear', instruction: 'Name 4 things you can hear — near or far. Let each sound come to you.' },
  { n: 3, label: 'Touch', instruction: 'Notice 3 things you can physically feel — the chair, your feet, the texture of your clothes.' },
  { n: 2, label: 'Smell', instruction: 'Notice 2 things you can smell. You can also follow your breath.' },
  { n: 1, label: 'Taste', instruction: 'Notice 1 thing — a taste or the sensation in your mouth right now.' },
]

export default function GroundingExercise({ onDismiss }: GroundingExerciseProps) {
  const { t } = useTranslation()

  /*
   * A locale array is only trusted at the exact expected length. A short or
   * missing one falls back to the full English set rather than rendering a
   * half-empty exercise.
   */
  const localArray = (key: string, fallback: string[]) => {
    const v = t(key, { returnObjects: true })
    return Array.isArray(v) && v.length === fallback.length ? (v as string[]) : fallback
  }
  const breathPhaseLabels = localArray('grounding.breathPhases', BREATH_PHASE_LABELS)
  // Merged by position: only the label and instruction are language-dependent —
  // `n` is the count shown as a numeral and never moves.
  const senseLabels = localArray('grounding.senseLabels', SENSES_STEPS.map(s => s.label))
  const senseInstructions = localArray('grounding.sensesSteps', SENSES_STEPS.map(s => s.instruction))
  const sensesSteps = SENSES_STEPS.map((step, i) => ({
    n: step.n,
    label: senseLabels[i] ?? step.label,
    instruction: senseInstructions[i] ?? step.instruction,
  }))

  const [exerciseState, setExerciseState] = useState<ExerciseState>('choosing')
  useScreenId(SCREEN_OF[exerciseState])
  // Back: the exercise's own screens, in order. It is hosted by Close, so it is
  // asked before Close is, and Back leaves it only from its first screen.
  useScreenTrail<ExerciseState>({ screen: exerciseState, go: setExerciseState, depth: BACK_DEPTH.hosted })

  // ─── Box breath state ───────────────────────────────────────────────────────
  // We drive the entire animation from a single step counter (0–63).
  // Deriving phase/count/round from the step avoids nested setState race conditions.
  const [breathStep, setBreathStep] = useState(0)

  const breathPhaseIndex = Math.floor((breathStep % 16) / 4)  // 0-3 within each round
  const breathCount = 4 - (breathStep % 4)                    // countdown: 4, 3, 2, 1
  const breathRound = Math.floor(breathStep / 16) + 1         // 1-4
  const breathDone = breathStep >= BOX_BREATH_TOTAL_STEPS
  const circleSize = CIRCLE_SIZE_BY_PHASE[breathPhaseIndex]
  const circleTransition = CIRCLE_TRANSITION_BY_PHASE[breathPhaseIndex]

  useEffect(() => {
    // Advance step once per second while box breath is active and not finished
    if (exerciseState !== 'box-breath' || breathDone) return
    const timer = setTimeout(() => setBreathStep(s => s + 1), 1000)
    return () => clearTimeout(timer)
  }, [exerciseState, breathStep, breathDone])

  // ─── 5-4-3-2-1 state ───────────────────────────────────────────────────────
  const [senseStep, setSenseStep] = useState(0)
  const senseDone = senseStep >= sensesSteps.length
  // The five senses are one screen to the trail above but five steps to the
  // person. Back goes through them, and from the first hands over.
  useSessionBack(() => {
    if (exerciseState !== 'senses' || senseStep === 0) return 'first'
    setSenseStep(senseStep - 1)
    return 'handled'
  }, BACK_DEPTH.hostedStep)

  // ─── Choosing screen ──────────────────────────────────────────────────────────
  if (exerciseState === 'choosing') {
    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          <div className="mb-8">
            <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
              {t('grounding.beforeYouGo')}
            </p>
            <h2 className="font-display text-xl mb-3" style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic', lineHeight: 1.4 }}>
              {t('grounding.takeAMoment')}
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>
              {t('grounding.somethingMoved')}
            </p>
          </div>

          {/* Option 1: Box breath */}
          <button
            onClick={() => setExerciseState('box-breath')}
            className="px-5 py-4 rounded-2xl mb-3 text-left w-full transition-all"
            style={{
              background: 'rgba(155,114,192,0.03)',
              borderTop: '3px solid #9B72C0',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
              cursor: 'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderTopColor = '#7B52A0' }}
            onMouseLeave={e => { e.currentTarget.style.borderTopColor = '#9B72C0' }}
          >
            <p className="text-sm font-medium mb-1" style={{ color: '#1C1128' }}>{t('grounding.boxBreathing')}</p>
            <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A' }}>
              {t('grounding.boxBreathingDesc')}
            </p>
          </button>

          {/* Option 2: 5-4-3-2-1 senses */}
          <button
            onClick={() => setExerciseState('senses')}
            className="px-5 py-4 rounded-2xl mb-3 text-left w-full transition-all"
            style={{
              background: 'rgba(155,114,192,0.03)',
              borderTop: '3px solid #9B72C0',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
              cursor: 'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderTopColor = '#7B52A0' }}
            onMouseLeave={e => { e.currentTarget.style.borderTopColor = '#9B72C0' }}
          >
            <p className="text-sm font-medium mb-1" style={{ color: '#1C1128' }}>{t('grounding.fiveSensesTitle')}</p>
            <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A' }}>
              {t('grounding.fiveSensesDesc')}
            </p>
          </button>

          {/* Option 3: Feet + weight body scan */}
          <button
            onClick={() => setExerciseState('feet')}
            className="px-5 py-4 rounded-2xl mb-6 text-left w-full transition-all"
            style={{
              background: 'rgba(155,114,192,0.03)',
              borderTop: '3px solid #9B72C0',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
              cursor: 'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderTopColor = '#7B52A0' }}
            onMouseLeave={e => { e.currentTarget.style.borderTopColor = '#9B72C0' }}
          >
            <p className="text-sm font-medium mb-1" style={{ color: '#1C1128' }}>{t('grounding.bodyScan')}</p>
            <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A' }}>
              {t('grounding.bodyScanDesc')}
            </p>
          </button>

          <div className="flex-1" />

          <button
            onClick={onDismiss}
            className="w-full py-2 text-sm text-center"
            style={{ background: 'none', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
          >
            {t('grounding.skipForNow')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Box Breath screen ────────────────────────────────────────────────────────
  if (exerciseState === 'box-breath') {
    if (breathDone) {
      return <GroundingDoneScreen onDismiss={onDismiss} />
    }

    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full items-center">

          <p className="text-xs font-medium uppercase tracking-widest mb-10 self-start" style={{ color: '#9B72C0' }}>
            {t('grounding.boxBreathingRound', { n: breathRound })}
          </p>

          {/* Animated breathing circle */}
          <div className="flex flex-col items-center justify-center flex-1">
            <div
              style={{
                width: circleSize,
                height: circleSize,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(155,114,192,0.18) 0%, rgba(155,114,192,0.06) 100%)',
                border: '2px solid rgba(155,114,192,0.3)',
                transition: `width ${circleTransition}, height ${circleTransition}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span className="font-display text-3xl" style={{ color: '#9B72C0', fontWeight: 400 }}>
                {breathCount}
              </span>
            </div>

            <p className="mt-8 text-base font-display italic" style={{ color: '#1C1128' }}>
              {breathPhaseLabels[breathPhaseIndex]}
            </p>
          </div>

          <button
            onClick={onDismiss}
            className="w-full py-2 text-sm text-center mt-6"
            style={{ background: 'none', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
          >
            {t('common.skip')}
          </button>
        </div>
      </div>
    )
  }

  // ─── 5-4-3-2-1 Senses screen ─────────────────────────────────────────────────
  if (exerciseState === 'senses') {
    if (senseDone) {
      return <GroundingDoneScreen onDismiss={onDismiss} />
    }

    const current = sensesSteps[senseStep]

    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          <p className="text-xs font-medium uppercase tracking-widest mb-10" style={{ color: '#9B72C0' }}>
            {t('close.stepsCount', { current: senseStep + 1, total: sensesSteps.length })}
          </p>

          <div className="flex-1 flex flex-col justify-center">
            {/* Large sense number */}
            <p className="font-display mb-2" style={{ color: '#C4B5D4', fontSize: '4rem', fontWeight: 400, lineHeight: 1 }}>
              {current.n}
            </p>
            <p className="text-xs font-medium uppercase tracking-widest mb-6" style={{ color: '#9B72C0' }}>
              {current.label}
            </p>
            <p className="text-base leading-relaxed font-display italic" style={{ color: '#1C1128' }}>
              {current.instruction}
            </p>
          </div>

          <button
            onClick={() => setSenseStep(s => s + 1)}
            className="w-full py-4 rounded-xl font-medium tracking-wide mb-3"
            style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            {senseStep < sensesSteps.length - 1 ? t('common.next') : t('common.done')}
          </button>
          <button
            onClick={onDismiss}
            className="w-full py-2 text-sm text-center"
            style={{ background: 'none', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
          >
            {t('common.skip')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Feet + Weight body scan ──────────────────────────────────────────────────
  if (exerciseState === 'feet') {
    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          <p className="text-xs font-medium uppercase tracking-widest mb-10" style={{ color: '#9B72C0' }}>
            {t('grounding.bodyScan')}
          </p>

          <div className="flex-1 flex flex-col justify-center gap-6">
            <p className="text-base leading-relaxed font-display italic" style={{ color: '#1C1128' }}>
              {t('grounding.feetOnFloor')}
            </p>
            <p className="text-base leading-relaxed font-display italic" style={{ color: '#1C1128' }}>
              {t('grounding.weightOfBody')}
            </p>
            <p className="text-base leading-relaxed font-display italic" style={{ color: '#1C1128' }}>
              {t('grounding.airOnSkin')}
            </p>
            <p className="text-base leading-relaxed font-display italic" style={{ color: '#1C1128' }}>
              {t('grounding.stayHere')}
            </p>
          </div>

          <button
            onClick={() => setExerciseState('done')}
            className="w-full py-4 rounded-xl font-medium tracking-wide mb-3"
            style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            {t('grounding.imHere')}
          </button>
          <button
            onClick={onDismiss}
            className="w-full py-2 text-sm text-center"
            style={{ background: 'none', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
          >
            {t('common.skip')}
          </button>
        </div>
      </div>
    )
  }

  // exerciseState === 'done'
  return <GroundingDoneScreen onDismiss={onDismiss} />
}

/**
 * Shared completion screen — shown after any exercise finishes.
 * Sage spec copy verbatim: "You're back. Take what served you. Leave the rest."
 */
function GroundingDoneScreen({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex-1 flex flex-col page-enter">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 max-w-xl mx-auto w-full text-center">
        <p className="font-display text-xl mb-6" style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic', lineHeight: 1.5 }}>
          {t('grounding.youreBack')}
        </p>
        <p className="text-sm leading-relaxed mb-12" style={{ color: '#6B5F7A', lineHeight: 1.7 }}>
          {t('grounding.takeWhatServed')}
        </p>
        <button
          onClick={onDismiss}
          className="w-full py-4 rounded-xl font-medium tracking-wide"
          style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
        >
          {t('session.continue')}
        </button>
      </div>
    </div>
  )
}
